import { CapacitorHttp } from '@capacitor/core';
import axios from 'axios';
import getBaseURL from './utils/constants';
import secureTokenStorage from './utils/secureTokenStorage';

/**
 * Business error codes returned by the API inside a HTTP 200 body.
 * See org/Common/ErrorCodes.php.
 */
const F = {
  ACCESS_TOKEN_INVALID: 1002,
  ACCESS_TOKEN_EXPIRED: 1003, // retriable: refresh and try again
  ACCESS_DENIED: 1004,
  INVALID_IDENTITY: 1006, // institution/scholar context missing — recoverable
  REFRESH_TOKEN_INVALID: 1009, // the ONLY code that means "session is over"
  REFRESH_UNAVAILABLE: 1010, // server could not process the refresh — retry, keep session
};

const REFRESH_MAX_ATTEMPTS = 3;
const REFRESH_BASE_BACKOFF_MS = 800;
const REFRESH_COOLDOWN_MS = 5000;

/**
 * A transport or server-side failure. The session is probably fine, so this must
 * NEVER reach hardLogout().
 */
export class RetryableAuthError extends Error {
  constructor(reason) {
    super(`auth_retryable:${reason}`);
    this.name = 'RetryableAuthError';
    this.reason = reason;
    this.retryable = true;
  }
}

/**
 * The server explicitly told us the session is dead (f:1009). Only this may end
 * the session.
 */
export class AuthRejectedError extends Error {
  constructor(reason) {
    super(`auth_rejected:${reason}`);
    this.name = 'AuthRejectedError';
    this.reason = reason;
    this.retryable = false;
  }
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Was this a failure to reach the server / a server-side fault, rather than an
 * authentication decision?
 *
 * On native, CapacitorHttp resolves for EVERY status code (see the Android
 * HttpRequestHandler.buildResponse) so a 502 or 429 arrives looking like a normal
 * response with an unparseable body. Treating that as "refresh rejected" is what
 * logged people out during deploys and on flaky connections.
 */
const isTransportFailure = (status) => status === 0 || status === 429 || status >= 500 || status === undefined;

class SecureAPIClient {
  constructor() {
    this.baseURL = getBaseURL();
    this.isNativeApp = secureTokenStorage.isNativePlatform;
    // Single-flight refresh: one in-flight promise that every caller awaits.
    // Deliberately NOT a boolean flag plus a queue — two independent coordinators
    // is what allowed two rotations to race and revoke each other's tokens.
    this.refreshPromise = null;
    // Set after a refresh fails, so a burst of parallel 401/1003s cannot hammer
    // the endpoint or spin in a retry loop.
    this.refreshBlockedUntil = 0;
    // hardLogout() re-entrancy guard — prevents /sign-in redirect loops.
    this.loggingOut = false;
    // Set once a refresh has been proven not to clear an f:1004, i.e. it is a real
    // account denial rather than a rotation artifact. Stops every subsequent call
    // from triggering a pointless token rotation.
    this.accessDeniedIsFinal = false;

    this.axiosInstance = axios.create();

    this.axiosInstance.interceptors.response.use(
      response => response,
      async error => {
        const originalRequest = error.config;

        // Web-only path: a real HTTP 401. Native never gets here (CapacitorHttp
        // resolves instead of rejecting) — that path is handled in request().
        if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
          originalRequest._retry = true;

          try {
            const newToken = await this.refreshAccessToken();

            originalRequest.headers['Authorization'] = `Bearer ${newToken}`;

            return this.axiosInstance(originalRequest);
          } catch (refreshError) {
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );
  }

  /**
   * The one place a session is ever destroyed. Guarded so that N concurrent
   * failures produce exactly one logout, and only ever called for a confirmed
   * authentication rejection (f:1009).
   */
  async hardLogout(reason) {
    if (this.loggingOut) return;
    this.loggingOut = true;

    console.warn(`AUTH_EVENT event=logout reason=${reason} source=secureApi.hardLogout`);

    await secureTokenStorage.clearTokens();
    localStorage.removeItem('scholar-token');
    window.location.href = '/sign-in';
  }

  /**
   * Refresh the access token. Concurrent callers share one request.
   *
   * Resolves with the new access token, or rejects with:
   *   - AuthRejectedError  → the session is genuinely over (already logged out)
   *   - RetryableAuthError → could not complete; the session is left intact
   */
  async refreshAccessToken() {
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    if (Date.now() < this.refreshBlockedUntil) {
      throw new RetryableAuthError('refresh_cooldown');
    }

    this.refreshPromise = this.runRefreshWithRetry()
      .finally(() => {
        this.refreshPromise = null;
      });

    return this.refreshPromise;
  }

  async runRefreshWithRetry() {
    let lastError = null;

    for (let attempt = 1; attempt <= REFRESH_MAX_ATTEMPTS; attempt += 1) {
      try {
        return await this.performRefresh();
      } catch (error) {
        lastError = error;

        // A confirmed rejection is final — retrying cannot help.
        if (!error?.retryable) {
          throw error;
        }

        if (attempt < REFRESH_MAX_ATTEMPTS) {
          // Jittered backoff: a whole school reconnecting at once must not
          // synchronise into a thundering herd on /auth/refresh.
          const backoff = REFRESH_BASE_BACKOFF_MS * (2 ** (attempt - 1));

          await sleep(backoff + Math.random() * 300);
        }
      }
    }

    // Out of attempts. Hold off briefly, but keep the tokens: the server-side
    // rotation grace window means the same refresh token stays usable.
    this.refreshBlockedUntil = Date.now() + REFRESH_COOLDOWN_MS;
    console.warn(`AUTH_EVENT event=refresh_failed reason=${lastError?.reason || 'unknown'} source=secureApi.runRefreshWithRetry`);

    throw lastError || new RetryableAuthError('refresh_exhausted');
  }

  async performRefresh() {
    const refreshToken = await secureTokenStorage.getRefreshToken();

    // Send the token in the body on both platforms so the backend can use it even
    // when the HttpOnly cookie is not forwarded (native has no cookie jar).
    const refreshData = refreshToken ? { refresh_token: refreshToken } : {};

    let res;
    let status;

    try {
      if (this.isNativeApp) {
        const raw = await CapacitorHttp.post({
          url: `${this.baseURL}/auth/refresh`,
          headers: {
            'Content-Type': 'application/json',
            'X-Client-Platform': 'capacitor'
          },
          data: refreshData
        });

        status = raw.status;
        res = raw.data;
      } else {
        const response = await axios.post(`${this.baseURL}/auth/refresh`, refreshData);

        status = response.status;
        res = response.data;
      }
    } catch (error) {
      // Both paths can land here: axios rejects on non-2xx and on timeout, and
      // CapacitorHttp rejects on a genuine connection failure (it only *resolves*
      // for HTTP error statuses, which is what the check below is for).
      throw new RetryableAuthError(`transport_${error?.response?.status || 'network'}`);
    }

    // Native: an HTTP-level failure arrives as a resolved response, so it has to
    // be classified explicitly or it looks identical to a rejected session.
    if (isTransportFailure(status)) {
      throw new RetryableAuthError(`transport_${status}`);
    }

    if (res && res.s && res.d && res.d.access_token) {
      await secureTokenStorage.setTokens(res.d.access_token, res.d.refresh_token);

      // Keep the backward-compat key in sync so direct localStorage readers
      // (e.g. CampusKeyScholarMain PDF download) never hold a stale token.
      localStorage.setItem('scholar-token', res.d.access_token);

      if (res.d.inst_id) {
        localStorage.setItem('inst_id', res.d.inst_id);
      }
      if (res.d.scholar_id) {
        localStorage.setItem('scholar_id', res.d.scholar_id);
      }

      this.refreshBlockedUntil = 0;

      return res.d.access_token;
    }

    // The server confirmed this refresh token is dead. This is the only response
    // that may end the session.
    if (res && res.f === F.REFRESH_TOKEN_INVALID) {
      await this.hardLogout('refresh_rejected_1009');
      throw new AuthRejectedError('session_expired');
    }

    // The server could not process the refresh at all (database down, signing key
    // unreadable, deploy in progress). Says nothing about the token — retry.
    if (res && res.f === F.REFRESH_UNAVAILABLE) {
      throw new RetryableAuthError('refresh_unavailable_1010');
    }

    // A 2xx we cannot make sense of: an HTML error/maintenance page, a captive
    // portal, a truncated body. Not evidence the session is over.
    throw new RetryableAuthError('unexpected_refresh_payload');
  }

  /**
   * Ask the server to re-establish the institution/scholar context after f:1006.
   *
   * f:1006 means the server lost the Redis mapping for this session, not that the
   * credentials are bad — the access token is still valid. Deleting the tokens
   * here (as this used to) turned a recoverable cache miss into a full logout.
   */
  async recoverIdentity() {
    const instId = localStorage.getItem('inst_id');
    const scholarId = localStorage.getItem('scholar_id');

    if (!instId || !scholarId || instId === 'null' || scholarId === 'null') {
      return false;
    }

    try {
      const res = await this.rawRequest('/select-scholar', { inst_id: instId, scholar_id: scholarId }, 'POST');

      return !!(res && res.s);
    } catch (error) {
      return false;
    }
  }

  /**
   * One HTTP round trip with the current token. No refresh, no recovery, no
   * retry — those live in request() so this cannot recurse.
   */
  async rawRequest(api, data = {}, method = 'POST') {
    const url = `${this.baseURL}${api}`;
    const accessToken = await secureTokenStorage.getAccessToken();

    const headers = { 'Content-Type': 'application/json' };

    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }

    // Lets the backend reliably detect Capacitor apps.
    if (this.isNativeApp) {
      headers['X-Client-Platform'] = 'capacitor';
    }

    if (this.isNativeApp) {
      const raw = await CapacitorHttp.request({
        url,
        method,
        headers,
        data: method !== 'GET' ? data : undefined
      });

      if (isTransportFailure(raw.status)) {
        throw new RetryableAuthError(`transport_${raw.status}`);
      }

      return raw.data;
    }

    const response = method === 'GET'
      ? await this.axiosInstance.get(url, { headers })
      : await this.axiosInstance.post(url, data, { headers });

    return response.data;
  }

  async request(api, data = {}, method = 'POST') {
    let res = await this.rawRequest(api, data, method);

    // Token expired, or an access token revoked by a rotation that completed
    // while this request was in flight. Both are retriable after a refresh.
    //
    // Older API builds answered a rotation-revoked token with 1004; newer ones
    // send the retriable 1003. So 1004 is retried once too — but only until a
    // refresh has been shown not to fix it, otherwise a deactivated account would
    // trigger a full token rotation on every single request it makes.
    const isRetriableAuthCode =
      res && !res.s &&
      (res.f === F.ACCESS_TOKEN_EXPIRED ||
        res.f === F.ACCESS_TOKEN_INVALID ||
        (res.f === F.ACCESS_DENIED && !this.accessDeniedIsFinal));

    if (isRetriableAuthCode) {
      const wasDenied = res.f === F.ACCESS_DENIED;

      await this.refreshAccessToken();
      res = await this.rawRequest(api, data, method);

      // Still denied after a fresh token: this is a real account denial.
      if (wasDenied && res && !res.s && res.f === F.ACCESS_DENIED) {
        this.accessDeniedIsFinal = true;
        console.warn('AUTH_EVENT event=access_denied reason=persists_after_refresh source=secureApi.request');
      }
    }

    // Server-side session context lost. Re-establish it and retry once.
    if (res && !res.s && res.f === F.INVALID_IDENTITY) {
      const recovered = await this.recoverIdentity();

      if (recovered) {
        res = await this.rawRequest(api, data, method);
      } else {
        // Could not re-select. Surface it so the UI can send the user through
        // scholar selection — still NOT a logout: the credentials are valid.
        console.warn('AUTH_EVENT event=identity_lost reason=select_failed source=secureApi.request');
      }
    }

    return res;
  }

  async post(api, data = {}) {
    return this.request(api, data, 'POST');
  }

  async get(api) {
    return this.request(api, {}, 'GET');
  }
}

const secureApi = new SecureAPIClient();

export default secureApi;
